import { ReactNode } from "react";
import { KeyValueList } from "../../key-value-list";
import { cn } from "../../../utils/class-names";
import { Amount, getPointsLabel, PointsItem, Usd } from "@silo/types";
import { asFullAmount, asFullUsd } from "@silo/utils";
import { NonEmptyReadonlyArray } from "effect/Array";
import { isNotNullable } from "effect/Predicate";

interface ListProps {
  className?: string;
  children?: ReactNode;
  divider?: boolean;
}

export function List({ className, children, divider }: ListProps): JSX.Element {
  return (
    <KeyValueList
      divider={divider}
      className={cn("@[1350px]:flex-[3]", className)}
    >
      {children}
    </KeyValueList>
  );
}

interface BalancePairProps {
  className?: string;
  label: ReactNode;
  balance: Amount;
  balanceUsd: Usd;
  decimals: number;
}

export function BalancePair({
  className,
  label,
  balance,
  balanceUsd,
  decimals,
}: BalancePairProps): JSX.Element {
  return (
    <KeyValueList.Pair className={className}>
      <KeyValueList.Key>{label}</KeyValueList.Key>
      <KeyValueList.Value>
        <span data-cy="balance">{asFullAmount(balance, decimals)}</span>
        <span
          className="text-sm font-normal text-shale-200"
          data-cy="usd-balance"
        >
          {asFullUsd(balanceUsd)}
        </span>
      </KeyValueList.Value>
    </KeyValueList.Pair>
  );
}

interface PointsPairProps {
  className?: string;
  points: NonEmptyReadonlyArray<PointsItem>;
}

export function PointsPair({
  className,
  points,
}: PointsPairProps): JSX.Element {
  return (
    <KeyValueList.Pair className={className}>
      <KeyValueList.Key>Points</KeyValueList.Key>

      <KeyValueList.Value className="font-sans">
        {points.map((pointsItem) => (
          <div>{getPointsLabel(pointsItem)}</div>
        ))}
      </KeyValueList.Value>
    </KeyValueList.Pair>
  );
}

interface LiquidationPricePairProps {
  className?: string;
  liquidationPriceUsd: Usd | null;
  description: ReactNode;
}

export function LiquidationPricePair({
  className,
  liquidationPriceUsd,
  description,
}: LiquidationPricePairProps): JSX.Element {
  return (
    <KeyValueList.Pair className={className} description={description}>
      <KeyValueList.Key>Liquidation price</KeyValueList.Key>
      <KeyValueList.Value>
        {isNotNullable(liquidationPriceUsd)
          ? asFullUsd(liquidationPriceUsd)
          : "--"}
      </KeyValueList.Value>
    </KeyValueList.Pair>
  );
}
